import React, { useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import { MenuBook, OpenInNew } from '@mui/icons-material';
import { APP_BUILD_NUMBER } from '../config/appInfo';
import { getHelpPageLabel, getManualPdfUrl } from '../lib/helpContext';

export default function HelpPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'en' ? 'en' : 'nb';
  const build = searchParams.get('v') || APP_BUILD_NUMBER || 'local';
  const pageId = searchParams.get('page') || 'unknown';
  const pageLabel = getHelpPageLabel(pageId, lang);
  const pdfUrl = getManualPdfUrl(lang);
  const contextQuery = `lang=${lang}&v=${encodeURIComponent(build)}&page=${encodeURIComponent(pageId)}`;

  const copy = useMemo(
    () =>
      lang === 'en'
        ? {
            title: 'User guide',
            intro:
              'Open the OpenCare user guide in your preferred language. Build and page context are included for future in-app assistance.',
            openPdf: 'Open PDF',
            context: 'Help context',
            build: 'App build',
            page: 'Page',
            pageId: 'Page id',
            language: 'Language',
            switchNb: 'Norsk',
            switchEn: 'English',
            back: 'Back to home',
            note: 'Query parameters lang, v and page are the contract for a future AI assistant.'
          }
        : {
            title: 'Brukermanual',
            intro:
              'Åpne OpenCare-brukermanualen på ønsket språk. Build og sidekontekst følger med som grunnlag for fremtidig hjelp i appen.',
            openPdf: 'Åpne PDF',
            context: 'Hjelpekontekst',
            build: 'App-build',
            page: 'Side',
            pageId: 'Side-id',
            language: 'Språk',
            switchNb: 'Norsk',
            switchEn: 'English',
            back: 'Tilbake til hjem',
            note: 'Parametrene lang, v og page er kontrakten for en fremtidig AI-assistent.'
          },
    [lang]
  );

  const setLang = (nextLang) => {
    const next = new URLSearchParams(searchParams);
    next.set('lang', nextLang);
    if (!next.get('v')) {
      next.set('v', build);
    }
    if (!next.get('page')) {
      next.set('page', pageId);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <MenuBook color="primary" />
        <Typography variant="h4" component="h1" fontWeight={600}>
          {copy.title}
        </Typography>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {copy.intro}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {copy.context}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`${copy.language}: ${lang === 'en' ? 'English' : 'Norsk'}`} size="small" />
            <Chip label={`${copy.build}: ${build}`} size="small" variant="outlined" />
            <Chip label={`${copy.page}: ${pageLabel}`} size="small" variant="outlined" />
            <Chip label={`${copy.pageId}: ${pageId}`} size="small" variant="outlined" />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <Button
              variant={lang === 'nb' ? 'contained' : 'outlined'}
              onClick={() => setLang('nb')}
            >
              {copy.switchNb}
            </Button>
            <Button
              variant={lang === 'en' ? 'contained' : 'outlined'}
              onClick={() => setLang('en')}
            >
              {copy.switchEn}
            </Button>
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<OpenInNew />}
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mr: 1, mb: 1 }}
          >
            {copy.openPdf}
          </Button>
          <Button component={RouterLink} to="/" variant="text" sx={{ mb: 1 }}>
            {copy.back}
          </Button>

          <Alert severity="info" sx={{ mt: 2 }}>
            {copy.note}
            <Typography
              component="code"
              variant="caption"
              display="block"
              sx={{ mt: 1, fontFamily: 'monospace' }}
            >
              /hjelp?{contextQuery}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
}
